import React, { useState, useMemo } from 'react';
import { useI18n } from '../../context/I18nContext';
import './AuthorizedUserBlueprint.css';
import { AlertTriangle } from 'lucide-react';

// ===================== CONSTANTS =====================
const PLANNER_BUCKETS = {
  excellent: { className: 'au-pill-green' },
  good: { className: 'au-pill-lime' },
  fair: { className: 'au-pill-yellow' },
  needs: { className: 'au-pill-orange' },
};

const CHECKLIST_WEIGHTS = [12, 14, 15, 12, 15, 12, 10, 10];

const BUREAU_PROFILES = {
  merged: {
    label: '3-Bureau Summary',
    imported: 'Jul 07, 2025',
    ageMonths: 13,
    utilization: 68,
    paymentHistory: 99.1,
    revolvingAccounts: 2,
    openAccounts: 4,
    inquiries: 6,
    derogatories: 2
  },
  tu: {
    label: 'TransUnion',
    imported: 'Jul 07, 2025',
    ageMonths: 13,
    utilization: 71,
    paymentHistory: 99.0,
    revolvingAccounts: 2,
    openAccounts: 4,
    inquiries: 7,
    derogatories: 2
  },
  eq: {
    label: 'Equifax',
    imported: 'Jul 07, 2025',
    ageMonths: 17,
    utilization: 52,
    paymentHistory: 99.3,
    revolvingAccounts: 3,
    openAccounts: 5,
    inquiries: 4,
    derogatories: 1
  },
  ex: {
    label: 'Experian',
    imported: 'Jul 07, 2025',
    ageMonths: 11,
    utilization: 61,
    paymentHistory: 98.7,
    revolvingAccounts: 2,
    openAccounts: 4,
    inquiries: 5,
    derogatories: 2
  }
};

// ===================== RATING FUNCTIONS =====================
const formatAge = (months, t, AU) => {
  const years = Math.floor(months / 12);
  const rem = months % 12;
  return t(`${AU}.common.ageFormat`, {
    years,
    yearsPlural: years !== 1 ? 's' : '',
    months: rem
  });
};

const ageRating = (months) => {
  if (months >= 108) return 'excellent';
  if (months >= 84) return 'good';
  if (months >= 60) return 'fair';
  return 'needs';
};

const utilRating = (util) => {
  if (util < 10) return 'excellent';
  if (util < 30) return 'good';
  if (util < 50) return 'fair';
  return 'needs';
};

const paymentRating = (p) => {
  if (p >= 100) return 'excellent';
  if (p >= 99) return 'good';
  if (p >= 97) return 'fair';
  return 'needs';
};

const revolvingRating = (n) => {
  if (n >= 5) return 'excellent';
  if (n >= 3) return 'good';
  if (n === 2) return 'fair';
  return 'needs';
};

const openAccountsRating = (n) => {
  if (n >= 8) return 'excellent';
  if (n >= 6) return 'good';
  if (n >= 4) return 'fair';
  return 'needs';
};

const inquiryRating = (n) => {
  if (n <= 1) return 'excellent';
  if (n <= 3) return 'good';
  if (n <= 5) return 'fair';
  return 'needs';
};

const derogatoryRating = (n) => {
  if (n === 0) return 'excellent';
  if (n === 1) return 'fair';
  return 'needs';
};

const factorScore = (level) => {
  if (level === 'excellent') return 100;
  if (level === 'good') return 78;
  if (level === 'fair') return 55;
  return 25;
};

const markerPosition = (level) => {
  if (level === 'needs') return '9%';
  if (level === 'fair') return '31%';
  if (level === 'good') return '51%';
  return '79%';
};

// ===================== STATUS PILL COMPONENT =====================
const StatusPill = ({ rating, impact, t, AU }) => {
  const meta = PLANNER_BUCKETS[rating] || PLANNER_BUCKETS.needs;
  return (
    <span className={`au-status-pill ${meta.className}`}>
      {t(`${AU}.common.ratings.${rating}`)} {impact && `• ${impact}`}
    </span>
  );
};

// ===================== FACTOR CARD COMPONENT =====================
const FactorCard = ({ title, value, subtitle, rating, impact, canHelp, note, legendRight, t, AU }) => {
  return (
    <div className="au-factor-card">
      <div className="au-factor-top">
        <div className="au-factor-title">{title}</div>
        <StatusPill rating={rating} impact={impact} t={t} AU={AU} />
      </div>
      
      <div className="au-factor-value">{value}</div>
      <div className="au-factor-sub">{subtitle}</div>
      
      <div className="au-meter">
        <div className="au-marker" style={{ left: markerPosition(rating) }} />
        <div className="au-meter-bar">
          <div className="au-seg au-seg-needs" />
          <div className="au-seg au-seg-fair" />
          <div className="au-seg au-seg-good" />
          <div className="au-seg au-seg-excellent" />
        </div>
      </div>
      
      <div className="au-legend">
        <div>{t(`${AU}.common.canAuHelp`)}</div>
        <div><strong>{canHelp}</strong></div>
        <div>{t(`${AU}.common.guide`)}</div>
        <div>{legendRight}</div>
      </div>
      
      <div className="au-mini-note">{note}</div>
    </div>
  );
};

// ===================== CHECKLIST ITEM COMPONENT =====================
const ChecklistItem = ({ checked, onChange, title, description, weight }) => {
  return (
    <label className="au-check-item">
      <input 
        type="checkbox" 
        checked={checked} 
        onChange={onChange}
        data-weight={weight}
      />
      <div>
        <h4>{title}</h4>
        <p>{description}</p>
      </div>
    </label>
  );
};

// ===================== QUALITY RING COMPONENT =====================
const QualityRing = ({ grade, label, pct, color, readinessScore, t, AU }) => {
  return (
    <div className="au-ring-card">
      <div 
        className="au-ring" 
        style={{ 
          '--ring-pct': pct,
          '--ring-color': color 
        }}
      >
        <div className="au-ring-inner">
          <div>
            <div className="au-grade">{grade}</div>
            <div className="au-grade-label">{label}</div>
            <div className="au-readiness-line">{t(`${AU}.common.readiness`)}: {readinessScore}%</div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ===================== MAIN COMPONENT =====================
const AuthorizedUserBlueprint = () => {
  const { t } = useI18n();
  const AU = 'creditBuilder.tradelineStrategy.authorizedUser';
  const [currentBureau, setCurrentBureau] = useState('merged');
  const [checklistState, setChecklistState] = useState({
    0: true, 1: true, 2: true, 3: true, 4: false, 5: false, 6: false, 7: false
  });
  
  const [auAge, setAuAge] = useState('84');
  const [auLimit, setAuLimit] = useState('15000');
  const [auUtil, setAuUtil] = useState('6');
  const [auHistory, setAuHistory] = useState('perfect');
  const [auCardType, setAuCardType] = useState('personal');
  const [auReporting, setAuReporting] = useState('strong');
  const [auHistoryMode, setAuHistoryMode] = useState('new');
  const [auReportsTo, setAuReportsTo] = useState('all');

  const bureauLabel = (key) => t(`${AU}.common.bureaus.${key}`);

  const checklistItems = useMemo(() => {
    return CHECKLIST_WEIGHTS.map((weight, idx) => ({
      title: t(`${AU}.readiness.items.${idx}.title`),
      description: t(`${AU}.readiness.items.${idx}.description`),
      weight
    }));
  }, [t, AU]);
  
  const profile = BUREAU_PROFILES[currentBureau];
  const profileBureauLabel = bureauLabel(currentBureau);
  
  const readinessScore = useMemo(() => {
    let totalWeight = 0;
    let earned = 0;
    let checkedCount = 0;
    
    checklistItems.forEach((item, idx) => {
      totalWeight += item.weight;
      if (checklistState[idx]) {
        earned += item.weight;
        checkedCount += 1;
      }
    });
    
    return {
      pct: Math.round((earned / totalWeight) * 100),
      checkedCount,
      total: checklistItems.length
    };
  }, [checklistState, checklistItems]);
  
  const qualityScore = useMemo(() => {
    const ageMonths = Number(auAge);
    const limit = Number(auLimit || 0);
    const util = Number(auUtil || 0);
    
    let ageScore = ageMonths >= 108 ? 25 : ageMonths >= 84 ? 20 : ageMonths >= 60 ? 14 : 6;
    let utilScore = util < 10 ? 25 : util < 30 ? 20 : util < 50 ? 12 : 4;
    let historyScore = auHistory === 'perfect' ? 25 : auHistory === 'olderlate' ? 15 : 2;
    let limitScore = limit >= 15000 ? 10 : limit >= 8000 ? 8 : limit >= 3000 ? 5 : 2;
    let reportingScore = auReporting === 'strong' ? 10 : auReporting === 'mixed' ? 6 : auReporting === 'unknown' ? 3 : 0;
    let historyModeScore = auHistoryMode === 'full' ? 5 : auHistoryMode === 'unknown' ? 2 : 0;
    
    return ageScore + utilScore + historyScore + limitScore + reportingScore + historyModeScore;
  }, [auAge, auLimit, auUtil, auHistory, auReporting, auHistoryMode]);
  
  const hardBlockers = useMemo(() => {
    const blockers = [];
    
    if (auReporting === 'no') blockers.push(t(`${AU}.qualityFilter.blockers.noReport`));
    if (auCardType !== 'personal') blockers.push(t(`${AU}.qualityFilter.blockers.businessRisk`));
    if (auHistory === 'recentlate') blockers.push(t(`${AU}.qualityFilter.blockers.recentLate`));
    if (Number(auUtil) >= 50) blockers.push(t(`${AU}.qualityFilter.blockers.highUtil`));
    if (!checklistState[1]) blockers.push(t(`${AU}.qualityFilter.blockers.reportingUnverified`));
    if (!checklistState[4]) blockers.push(t(`${AU}.qualityFilter.blockers.idIncomplete`));
    
    const profileAgeWeak = ageRating(profile.ageMonths) === 'needs' || ageRating(profile.ageMonths) === 'fair';
    if (profileAgeWeak && auHistoryMode === 'new') {
      blockers.push(t(`${AU}.qualityFilter.blockers.ageNewReport`));
    }
    
    return blockers;
  }, [auReporting, auCardType, auHistory, auUtil, auHistoryMode, checklistState, profile.ageMonths, t, AU]);
  
  const qualityPresentation = useMemo(() => {
    let grade = 'D';
    let label = t(`${AU}.qualityFilter.grades.weak`);
    let pct = 32;
    let color = '#fb7185';
    
    if (qualityScore >= 85) {
      grade = 'A';
      label = readinessScore.pct >= 85 && hardBlockers.length === 0
        ? t(`${AU}.qualityFilter.grades.bestFit`)
        : t(`${AU}.qualityFilter.grades.strongVerify`);
      pct = 88;
      color = '#10b981';
    } else if (qualityScore >= 70) {
      grade = 'B';
      label = t(`${AU}.qualityFilter.grades.conditional`);
      pct = 72;
      color = '#84cc16';
    } else if (qualityScore >= 55) {
      grade = 'C';
      label = t(`${AU}.qualityFilter.grades.average`);
      pct = 58;
      color = '#fbbf24';
    } else {
      grade = 'D';
      label = t(`${AU}.qualityFilter.grades.weak`);
      pct = 36;
      color = '#fb923c';
    }
    
    if (hardBlockers.length > 0) {
      if (qualityScore >= 70) {
        grade = 'B-';
        label = t(`${AU}.qualityFilter.grades.goodNotReady`);
        pct = 66;
        color = '#f59e0b';
      } else {
        grade = 'D';
        label = t(`${AU}.qualityFilter.grades.doNotRely`);
        pct = 34;
        color = '#fb7185';
      }
    }
    
    return { grade, label, pct, color };
  }, [qualityScore, readinessScore.pct, hardBlockers.length, t, AU]);
  
  const finalRecommendation = useMemo(() => {
    const ageWeak = ageRating(profile.ageMonths) === 'needs' || ageRating(profile.ageMonths) === 'fair';
    const utilWeak = utilRating(profile.utilization) === 'needs' || utilRating(profile.utilization) === 'fair';
    const negWeak = derogatoryRating(profile.derogatories) !== 'excellent';
    const paymentWeak = paymentRating(profile.paymentHistory) !== 'excellent';
    
    if (hardBlockers.length > 0 || qualityScore < 55) {
      const blockerPart = hardBlockers.length
        ? t(`${AU}.final.text.doNotUseBlocker`, { blocker: hardBlockers[0] })
        : '';
      return {
        recommendation: t(`${AU}.final.recommendations.doNotUse`),
        className: 'au-pill-red',
        text: t(`${AU}.final.text.doNotUse`, { blocker: blockerPart })
      };
    }
    
    if (negWeak || paymentWeak) {
      const weakness = negWeak
        ? t(`${AU}.final.text.weaknessNegatives`)
        : t(`${AU}.final.text.weaknessPayment`);
      return {
        recommendation: t(`${AU}.final.recommendations.verifyFirst`),
        className: 'au-pill-yellow',
        text: t(`${AU}.final.text.verifyNegPayment`, { weakness })
      };
    }
    
    if (readinessScore.pct < 70) {
      return {
        recommendation: t(`${AU}.final.recommendations.verifyFirst`),
        className: 'au-pill-gold',
        text: t(`${AU}.final.text.verifyIncomplete`)
      };
    }
    
    if (utilWeak) {
      return {
        recommendation: t(`${AU}.final.recommendations.addNow`),
        className: 'au-pill-green',
        text: t(`${AU}.final.text.addNowUtil`, { bureau: profileBureauLabel })
      };
    }
    
    if (ageWeak) {
      if (auHistoryMode === 'full') {
        return {
          recommendation: t(`${AU}.final.recommendations.addNow`),
          className: 'au-pill-green',
          text: t(`${AU}.final.text.addNowAgeFull`)
        };
      }
      return {
        recommendation: t(`${AU}.final.recommendations.verifyFirst`),
        className: 'au-pill-yellow',
        text: t(`${AU}.final.text.verifyAge`)
      };
    }
    
    return {
      recommendation: t(`${AU}.final.recommendations.verifyFirst`),
      className: 'au-pill-gold',
      text: t(`${AU}.final.text.verifySupportive`, { bureau: profileBureauLabel })
    };
  }, [profile, qualityScore, readinessScore.pct, hardBlockers, auHistoryMode, profileBureauLabel, t, AU]);
  
  const qualityChips = useMemo(() => {
    const chips = [];
    const ageMonths = Number(auAge);
    const util = Number(auUtil);
    const limit = Number(auLimit);
    
    if (ageMonths >= 84) chips.push(t(`${AU}.qualityFilter.chips.oldAccount`));
    else if (ageMonths >= 48) chips.push(t(`${AU}.qualityFilter.chips.fairAge`));
    else chips.push(t(`${AU}.qualityFilter.chips.newAccount`));
    
    if (util < 10) chips.push(t(`${AU}.qualityFilter.chips.lowUtil`));
    else if (util < 30) chips.push(t(`${AU}.qualityFilter.chips.acceptableUtil`));
    else chips.push(t(`${AU}.qualityFilter.chips.highUtilRisk`));
    
    if (auHistory === 'perfect') chips.push(t(`${AU}.qualityFilter.chips.perfectHistory`));
    else if (auHistory === 'olderlate') chips.push(t(`${AU}.qualityFilter.chips.olderLate`));
    else chips.push(t(`${AU}.qualityFilter.chips.recentLate`));
    
    if (limit >= 10000) chips.push(t(`${AU}.qualityFilter.chips.meaningfulLimit`));
    if (auHistoryMode === 'new') chips.push(t(`${AU}.qualityFilter.chips.ageReduced`));
    if (auHistoryMode === 'unknown') chips.push(t(`${AU}.qualityFilter.chips.historyUnverified`));
    if (auReportsTo === 'partial') chips.push(t(`${AU}.qualityFilter.chips.partialCoverage`));
    if (!checklistState[5]) chips.push(t(`${AU}.qualityFilter.chips.addressMissing`));
    
    return chips;
  }, [auAge, auUtil, auLimit, auHistory, auHistoryMode, auReportsTo, checklistState, t, AU]);
  
  const profileAnalysis = useMemo(() => {
    const factors = [
      { key: 'age', title: t(`${AU}.profile.factorKeys.age`), rating: ageRating(profile.ageMonths), score: factorScore(ageRating(profile.ageMonths)) },
      { key: 'util', title: t(`${AU}.profile.factorKeys.util`), rating: utilRating(profile.utilization), score: factorScore(utilRating(profile.utilization)) },
      { key: 'payment', title: t(`${AU}.profile.factorKeys.payment`), rating: paymentRating(profile.paymentHistory), score: factorScore(paymentRating(profile.paymentHistory)) },
      { key: 'revolving', title: t(`${AU}.profile.factorKeys.revolving`), rating: revolvingRating(profile.revolvingAccounts), score: factorScore(revolvingRating(profile.revolvingAccounts)) },
      { key: 'open', title: t(`${AU}.profile.factorKeys.open`), rating: openAccountsRating(profile.openAccounts), score: factorScore(openAccountsRating(profile.openAccounts)) },
      { key: 'negatives', title: t(`${AU}.profile.factorKeys.negatives`), rating: derogatoryRating(profile.derogatories), score: factorScore(derogatoryRating(profile.derogatories)) },
      { key: 'inquiries', title: t(`${AU}.profile.factorKeys.inquiries`), rating: inquiryRating(profile.inquiries), score: factorScore(inquiryRating(profile.inquiries)) },
    ];
    
    const sortedWeaknesses = [...factors].sort((a, b) => a.score - b.score);
    return { factors, sortedWeaknesses };
  }, [profile, t, AU]);
  
  const helpAnalysis = useMemo(() => {
    const mayHelp = [];
    const cannotFix = [];
    
    if (utilRating(profile.utilization) === 'needs' || utilRating(profile.utilization) === 'fair') {
      mayHelp.push(t(`${AU}.help.mayHelp.util`));
    }
    
    if (ageRating(profile.ageMonths) === 'needs' || ageRating(profile.ageMonths) === 'fair') {
      mayHelp.push(t(`${AU}.help.mayHelp.age`));
    }
    
    if (revolvingRating(profile.revolvingAccounts) === 'needs' || revolvingRating(profile.revolvingAccounts) === 'fair') {
      mayHelp.push(t(`${AU}.help.mayHelp.revolving`));
    }
    
    if (openAccountsRating(profile.openAccounts) === 'needs' || openAccountsRating(profile.openAccounts) === 'fair') {
      mayHelp.push(t(`${AU}.help.mayHelp.thickness`));
    }
    
    if (!mayHelp.length) {
      mayHelp.push(t(`${AU}.help.mayHelp.fallback`));
    }
    
    if (derogatoryRating(profile.derogatories) !== 'excellent') {
      cannotFix.push(t(`${AU}.help.cannotFix.derogatories`));
    }
    
    if (paymentRating(profile.paymentHistory) !== 'excellent') {
      cannotFix.push(t(`${AU}.help.cannotFix.payment`));
    }
    
    if (inquiryRating(profile.inquiries) === 'needs' || inquiryRating(profile.inquiries) === 'fair') {
      cannotFix.push(t(`${AU}.help.cannotFix.inquiries`));
    }
    
    cannotFix.push(t(`${AU}.help.cannotFix.primary`));
    
    return { mayHelp, cannotFix };
  }, [profile, t, AU]);

  const qualityCallout = useMemo(() => {
    let text = qualityScore >= 85
      ? t(`${AU}.qualityFilter.callout.strong`)
      : qualityScore >= 70
        ? t(`${AU}.qualityFilter.callout.usable`)
        : qualityScore >= 55
          ? t(`${AU}.qualityFilter.callout.average`)
          : t(`${AU}.qualityFilter.callout.weak`);
    if (auHistoryMode === 'new') text += t(`${AU}.qualityFilter.callout.ageReduced`);
    if (auHistoryMode === 'unknown') text += t(`${AU}.qualityFilter.callout.ageUnknown`);
    if (auHistoryMode === 'full') text += t(`${AU}.qualityFilter.callout.ageFull`);
    return text;
  }, [qualityScore, auHistoryMode, t, AU]);
  
  const handleChecklistChange = (idx) => {
    setChecklistState(prev => ({ ...prev, [idx]: !prev[idx] }));
  };
  
  return (
    <div className="au-blueprint-page">
      <section className="au-hero" id="overview">
        <div className="au-hero-grid">
          <div>
            <div className="au-eyebrow">
              <span className="au-logo-dot" />
              {t(`${AU}.hero.eyebrow`)}
            </div>
            <h2 className="au-hero-title">{t(`${AU}.hero.title`)}</h2>
            <p className="au-hero-desc">
              {t(`${AU}.hero.description.intro`)}
              <strong>{t(`${AU}.hero.description.weaknesses`)}</strong>
              {t(`${AU}.hero.description.comma1`)}
              <strong>{t(`${AU}.hero.description.auQuality`)}</strong>
              {t(`${AU}.hero.description.comma2`)}
              <strong>{t(`${AU}.hero.description.readiness`)}</strong>
              {t(`${AU}.hero.description.riskLead`)}
              <strong>{t(`${AU}.hero.description.noFullHistory`)}</strong>
              {t(`${AU}.hero.description.riskMid`)}
              <strong>{t(`${AU}.hero.description.newFromDate`)}</strong>
              {t(`${AU}.hero.description.end`)}
            </p>
            
            <div className="au-hero-badges">
              <div className="au-badge"><span className="au-dot" /> {t(`${AU}.hero.badges.bureauView`)}</div>
              <div className="au-badge"><span className="au-dot" /> {t(`${AU}.hero.badges.dynamicDiagnosis`)}</div>
              <div className="au-badge"><span className="au-dot" /> {t(`${AU}.hero.badges.auQualityReadiness`)}</div>
              <div className="au-badge"><span className="au-dot" /> {t(`${AU}.hero.badges.finalRecommendation`)}</div>
            </div>
            
            <div className="au-warning-banner">
              <div className="au-warning-icon">
                <AlertTriangle size={18} />
              </div>
              <div>
                <strong>{t(`${AU}.hero.warning.title`)}</strong> {t(`${AU}.hero.warning.body`)}
              </div>
            </div>
          </div>
          
          <div className="au-hero-side">
            <div className="au-mini-card">
              <h3>{t(`${AU}.hero.side.mayHelpTitle`)}</h3>
              <p>{t(`${AU}.hero.side.mayHelpBody`)}</p>
            </div>
            <div className="au-mini-card">
              <h3>{t(`${AU}.hero.side.doesNotHideTitle`)}</h3>
              <p>{t(`${AU}.hero.side.doesNotHideBody`)}</p>
            </div>
            <div className="au-mini-card">
              <h3>{t(`${AU}.hero.side.bestUseTitle`)}</h3>
              <p>{t(`${AU}.hero.side.bestUseBody`)}</p>
            </div>
          </div>
        </div>
      </section>
      
      <section className="au-section" id="profile">
        <div className="au-card au-full-width">
          <div className="au-row">
            <div>
              <h3 className="au-section-title">{t(`${AU}.profile.title`)}</h3>
              <p className="au-section-copy">{t(`${AU}.profile.subtitle`)}</p>
            </div>
            <div className="au-toggle-wrap">
              {Object.keys(BUREAU_PROFILES).map(key => (
                <button
                  key={key}
                  className={`au-toggle ${currentBureau === key ? 'active' : ''}`}
                  onClick={() => setCurrentBureau(key)}
                >
                  {bureauLabel(key)}
                </button>
              ))}
            </div>
          </div>
          <div className="au-subtle">
            {t(`${AU}.common.basedOnProfile`, { label: profileBureauLabel, date: profile.imported })}
          </div>
        </div>
        
        <div className="au-card au-full-width">
          <div className="au-factor-grid">
            <FactorCard
              title={t(`${AU}.profile.factors.creditAge.title`)}
              value={formatAge(profile.ageMonths, t, AU)}
              subtitle={t(`${AU}.profile.factors.creditAge.subtitle`)}
              rating={ageRating(profile.ageMonths)}
              impact={t(`${AU}.common.impacts.medium`)}
              canHelp={t(`${AU}.profile.factors.creditAge.canHelp`)}
              note={t(`${AU}.profile.factors.creditAge.note`)}
              legendRight={t(`${AU}.profile.factors.creditAge.legend`)}
              t={t}
              AU={AU}
            />
            <FactorCard
              title={t(`${AU}.profile.factors.utilization.title`)}
              value={`${profile.utilization}%`}
              subtitle={t(`${AU}.profile.factors.utilization.subtitle`)}
              rating={utilRating(profile.utilization)}
              impact={t(`${AU}.common.impacts.high`)}
              canHelp={t(`${AU}.profile.factors.utilization.canHelp`)}
              note={t(`${AU}.profile.factors.utilization.note`)}
              legendRight={t(`${AU}.profile.factors.utilization.legend`)}
              t={t}
              AU={AU}
            />
            <FactorCard
              title={t(`${AU}.profile.factors.paymentHistory.title`)}
              value={`${profile.paymentHistory.toFixed(1)}%`}
              subtitle={t(`${AU}.profile.factors.paymentHistory.subtitle`)}
              rating={paymentRating(profile.paymentHistory)}
              impact={t(`${AU}.common.impacts.veryHigh`)}
              canHelp={t(`${AU}.profile.factors.paymentHistory.canHelp`)}
              note={t(`${AU}.profile.factors.paymentHistory.note`)}
              legendRight={t(`${AU}.profile.factors.paymentHistory.legend`)}
              t={t}
              AU={AU}
            />
            <FactorCard
              title={t(`${AU}.profile.factors.revolvingDepth.title`)}
              value={`${profile.revolvingAccounts}`}
              subtitle={t(`${AU}.profile.factors.revolvingDepth.subtitle`)}
              rating={revolvingRating(profile.revolvingAccounts)}
              impact={t(`${AU}.common.impacts.medium`)}
              canHelp={t(`${AU}.profile.factors.revolvingDepth.canHelp`)}
              note={t(`${AU}.profile.factors.revolvingDepth.note`)}
              legendRight={t(`${AU}.profile.factors.revolvingDepth.legend`)}
              t={t}
              AU={AU}
            />
            <FactorCard
              title={t(`${AU}.profile.factors.fileThickness.title`)}
              value={`${profile.openAccounts}`}
              subtitle={t(`${AU}.profile.factors.fileThickness.subtitle`)}
              rating={openAccountsRating(profile.openAccounts)}
              impact={t(`${AU}.common.impacts.medium`)}
              canHelp={t(`${AU}.profile.factors.fileThickness.canHelp`)}
              note={t(`${AU}.profile.factors.fileThickness.note`)}
              legendRight={t(`${AU}.profile.factors.fileThickness.legend`)}
              t={t}
              AU={AU}
            />
            <FactorCard
              title={t(`${AU}.profile.factors.negativeItems.title`)}
              value={`${profile.derogatories}`}
              subtitle={t(`${AU}.profile.factors.negativeItems.subtitle`)}
              rating={derogatoryRating(profile.derogatories)}
              impact={t(`${AU}.common.impacts.high`)}
              canHelp={t(`${AU}.profile.factors.negativeItems.canHelp`)}
              note={t(`${AU}.profile.factors.negativeItems.note`)}
              legendRight={t(`${AU}.profile.factors.negativeItems.legend`)}
              t={t}
              AU={AU}
            />
          </div>
        </div>
      </section>
      
      <section className="au-section" id="help">
        <div className="au-card au-full-width">
          <h3 className="au-section-title">{t(`${AU}.help.title`)}</h3>
          <p className="au-section-copy">{t(`${AU}.help.subtitle`)}</p>
          
          <div className="au-summary-grid">
            <div className="au-summary-box">
              <h4>{t(`${AU}.help.mayHelpTitle`)}</h4>
              <div className="au-bullets">
                {helpAnalysis.mayHelp.map((item, idx) => (
                  <div key={idx} className="au-bullet">
                    <span>•</span>{item}
                  </div>
                ))}
              </div>
            </div>
            
            <div className="au-summary-box">
              <h4>{t(`${AU}.help.doesNotFixTitle`)}</h4>
              <div className="au-bullets">
                {helpAnalysis.cannotFix.map((item, idx) => (
                  <div key={idx} className="au-bullet">
                    <span>•</span>{item}
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <div className="au-diagnosis">
            <strong>{t(`${AU}.help.diagnosis`)}</strong>{' '}
            {t(`${AU}.help.diagnosisBody`, {
              bureau: profileBureauLabel,
              weak1: profileAnalysis.sortedWeaknesses[0]?.title,
              weak2: profileAnalysis.sortedWeaknesses[1]?.title
            })}
          </div>
        </div>
      </section>
      
      <section className="au-section au-two-col" id="readiness">
        <div className="au-card au-col-7">
          <h3 className="au-section-title">{t(`${AU}.readiness.title`)}</h3>
          <p className="au-section-copy">{t(`${AU}.readiness.subtitle`)}</p>
          
          <div className="au-checklist">
            {checklistItems.map((item, idx) => (
              <ChecklistItem
                key={idx}
                checked={checklistState[idx]}
                onChange={() => handleChecklistChange(idx)}
                title={item.title}
                description={item.description}
                weight={item.weight}
              />
            ))}
          </div>
          
          <div className="au-progress-wrap">
            <div className="au-progress-top">
              <strong>{t(`${AU}.readiness.progress`)}</strong>
              <span>{t(`${AU}.readiness.progressCount`, { checked: readinessScore.checkedCount, total: readinessScore.total })}</span>
            </div>
            <div className="au-progress-bar">
              <div 
                className="au-progress-fill" 
                style={{ width: `${readinessScore.pct}%` }}
              />
            </div>
          </div>
        </div>
        
        <div className="au-card au-col-5" id="quality">
          <h3 className="au-section-title">{t(`${AU}.qualityFilter.title`)}</h3>
          <p className="au-section-copy">{t(`${AU}.qualityFilter.subtitle`)}</p>
          
          <div className="au-controls">
            <div className="au-field">
              <label>{t(`${AU}.qualityFilter.fields.auAge`)}</label>
              <select value={auAge} onChange={(e) => setAuAge(e.target.value)}>
                <option value="18">{t(`${AU}.qualityFilter.options.ageNew`)}</option>
                <option value="48">{t(`${AU}.qualityFilter.options.ageFair`)}</option>
                <option value="84">{t(`${AU}.qualityFilter.options.ageGood`)}</option>
                <option value="132">{t(`${AU}.qualityFilter.options.ageOld`)}</option>
              </select>
            </div>
            
            <div className="au-field">
              <label>{t(`${AU}.qualityFilter.fields.auLimit`)}</label>
              <input type="number" min="500" step="500" value={auLimit} onChange={(e) => setAuLimit(e.target.value)} />
            </div>
            
            <div className="au-field">
              <label>{t(`${AU}.qualityFilter.fields.auUtil`)}</label>
              <input type="number" min="0" max="100" step="1" value={auUtil} onChange={(e) => setAuUtil(e.target.value)} />
            </div>
            
            <div className="au-field">
              <label>{t(`${AU}.qualityFilter.fields.paymentHistory`)}</label>
              <select value={auHistory} onChange={(e) => setAuHistory(e.target.value)}>
                <option value="perfect">{t(`${AU}.qualityFilter.options.historyPerfect`)}</option>
                <option value="olderlate">{t(`${AU}.qualityFilter.options.historyOlderLate`)}</option>
                <option value="recentlate">{t(`${AU}.qualityFilter.options.historyRecentLate`)}</option>
              </select>
            </div>
            
            <div className="au-field">
              <label>{t(`${AU}.qualityFilter.fields.cardType`)}</label>
              <select value={auCardType} onChange={(e) => setAuCardType(e.target.value)}>
                <option value="personal">{t(`${AU}.qualityFilter.options.cardPersonal`)}</option>
                <option value="business">{t(`${AU}.qualityFilter.options.cardBusiness`)}</option>
              </select>
            </div>
            
            <div className="au-field">
              <label>{t(`${AU}.qualityFilter.fields.reportingStrength`)}</label>
              <select value={auReporting} onChange={(e) => setAuReporting(e.target.value)}>
                <option value="strong">{t(`${AU}.qualityFilter.options.reportStrong`)}</option>
                <option value="mixed">{t(`${AU}.qualityFilter.options.reportMixed`)}</option>
                <option value="unknown">{t(`${AU}.qualityFilter.options.reportUnknown`)}</option>
                <option value="no">{t(`${AU}.qualityFilter.options.reportNo`)}</option>
              </select>
            </div>
            
            <div className="au-field">
              <label>{t(`${AU}.qualityFilter.fields.historyMode`)}</label>
              <select value={auHistoryMode} onChange={(e) => setAuHistoryMode(e.target.value)}>
                <option value="full">{t(`${AU}.qualityFilter.options.historyFull`)}</option>
                <option value="new">{t(`${AU}.qualityFilter.options.historyNew`)}</option>
                <option value="unknown">{t(`${AU}.qualityFilter.options.historyUnconfirmed`)}</option>
              </select>
            </div>
            
            <div className="au-field">
              <label>{t(`${AU}.qualityFilter.fields.bureauCoverage`)}</label>
              <select value={auReportsTo} onChange={(e) => setAuReportsTo(e.target.value)}>
                <option value="all">{t(`${AU}.qualityFilter.options.coverageAll`)}</option>
                <option value="partial">{t(`${AU}.qualityFilter.options.coveragePartial`)}</option>
                <option value="unknown">{t(`${AU}.qualityFilter.options.coverageUnknown`)}</option>
              </select>
            </div>
          </div>
          
          <div className="au-quality-grid">
            <QualityRing 
              grade={qualityPresentation.grade}
              label={qualityPresentation.label}
              pct={qualityPresentation.pct}
              color={qualityPresentation.color}
              readinessScore={readinessScore.pct}
              t={t}
              AU={AU}
            />
            
            <div className="au-quality-right">
              <div className="au-quality-score-row">
                <div className="au-score-box">
                  <div className="au-score-label">{t(`${AU}.qualityFilter.tradelineQuality`)}</div>
                  <div className="au-score-value">{qualityScore}</div>
                </div>
                <div className="au-score-box">
                  <div className="au-score-label">{t(`${AU}.qualityFilter.readinessScore`)}</div>
                  <div className="au-score-value">{readinessScore.pct}%</div>
                </div>
              </div>
              
              <div className="au-quality-metric">
                <h4>{t(`${AU}.qualityFilter.hardBlockers`)}</h4>
                <p>{hardBlockers.length ? hardBlockers.join(' ') : t(`${AU}.qualityFilter.noBlockers`)}</p>
              </div>
              
              <div className="au-callout">
                {qualityCallout}
              </div>
            </div>
          </div>
          
          <div className="au-pill-row">
            {qualityChips.map((chip, idx) => (
              <span key={idx} className="au-plain-pill">{chip}</span>
            ))}
          </div>
        </div>
      </section>
      
      <section className="au-section" id="final">
        <div className="au-card au-full-width">
          <div className="au-final-box">
            <div className="au-final-top">
              <h3 className="au-final-title">{t(`${AU}.final.title`)}</h3>
              <span className={`au-status-pill ${finalRecommendation.className}`}>
                {finalRecommendation.recommendation}
              </span>
            </div>
            <div className="au-final-verdict">
              {finalRecommendation.text}
            </div>
          </div>
        </div>
      </section>
      
      <section className="au-section au-two-col" id="timeline">
        <div className="au-card au-col-6">
          <h3 className="au-section-title">{t(`${AU}.timeline.title`)}</h3>
          <p className="au-section-copy">{t(`${AU}.timeline.subtitle`)}</p>
          
          <div className="au-timeline">
            <div className="au-step">
              <div className="au-step-num">1</div>
              <div className="au-step-box">
                <h4>{t(`${AU}.timeline.steps.day0Title`)}</h4>
                <p>{t(`${AU}.timeline.steps.day0Body`)}</p>
              </div>
            </div>
            
            <div className="au-step">
              <div className="au-step-num">2</div>
              <div className="au-step-box">
                <h4>{t(`${AU}.timeline.steps.day1Title`)}</h4>
                <p>{t(`${AU}.timeline.steps.day1Body`)}</p>
              </div>
            </div>
            
            <div className="au-step">
              <div className="au-step-num">3</div>
              <div className="au-step-box">
                <h4>{t(`${AU}.timeline.steps.day30Title`)}</h4>
                <p>{t(`${AU}.timeline.steps.day30Body`)}</p>
              </div>
            </div>
            
            <div className="au-step">
              <div className="au-step-num">4</div>
              <div className="au-step-box">
                <h4>{t(`${AU}.timeline.steps.day45Title`)}</h4>
                <p>{t(`${AU}.timeline.steps.day45Body`)}</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="au-card au-col-6">
          <h3 className="au-section-title">{t(`${AU}.rules.title`)}</h3>
          <p className="au-section-copy">{t(`${AU}.rules.subtitle`)}</p>
          
          <div className="au-summary-box" style={{ marginTop: '14px' }}>
            <h4>{t(`${AU}.rules.rememberTitle`)}</h4>
            <div className="au-bullets">
              <div className="au-bullet"><span>•</span> {t(`${AU}.rules.bullets.b1`)}</div>
              <div className="au-bullet"><span>•</span> {t(`${AU}.rules.bullets.b2`)}</div>
              <div className="au-bullet"><span>•</span> {t(`${AU}.rules.bullets.b3`)}</div>
              <div className="au-bullet"><span>•</span> {t(`${AU}.rules.bullets.b4`)}</div>
              <div className="au-bullet"><span>•</span> {t(`${AU}.rules.bullets.b5`)}</div>
            </div>
          </div>
        </div>
      </section>
      
      <div className="au-footer-note">
        {t(`${AU}.footer.disclaimer`)}
      </div>
    </div>
  );
};

export default AuthorizedUserBlueprint;
